import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { PlanApiData } from 'src/app/pages/new-billing/types/api.types';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';

export type AddonIds = {
  monthly: string;
  yearly: string;
  quarterly: string;
  semi_yearly: string;
};

export interface AddonPricingResult {
  calculatedPrice: string;
  calculatedSubtitle: string;
  unitPrice?: number;
  billingPeriod?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AddonPricingService {
  constructor(
    private readonly localizedPricingService: LocalizedPricingService,
    private readonly translate: TranslateService,
  ) {}
  private readonly mapRecurrenceKey: Record<string, keyof AddonIds> = {
    year: 'yearly',
    yearly: 'yearly',
    annual: 'yearly',
    annually: 'yearly',
    month: 'monthly',
    monthly: 'monthly',
    quarter: 'quarterly',
    quarterly: 'quarterly',
    semi_year: 'semi_yearly',
    semi_yearly: 'semi_yearly',
    'semi-year': 'semi_yearly',
    'semi-annual': 'semi_yearly',
    semiannual: 'semi_yearly',
  };

  /**
   * Calculates addon pricing based on the current plan's subscription recurrence
   * @param addonIds - The addon IDs for different billing periods
   * @param currentPlan - The current plan with billing API data
   * @param fallbackPrice - Fallback price if dynamic pricing fails
   * @param fallbackSubtitle - Fallback subtitle if dynamic pricing fails
   * @returns AddonPricingResult with calculated price and subtitle
   */
  public calculateAddonPricing(
    addonIds: AddonIds,
    currentPlan: ConcretePlan,
    fallbackPrice: string,
    fallbackSubtitle: string,
  ): AddonPricingResult {
    if (!addonIds || !currentPlan) {
      return {
        calculatedPrice: fallbackPrice,
        calculatedSubtitle: fallbackSubtitle,
      };
    }

    // Get the appropriate addon plan based on current plan's recurrence
    const addonPlan = this.getAddonPlanForCurrentRecurrence(addonIds, currentPlan);

    if (!addonPlan) {
      return {
        calculatedPrice: fallbackPrice,
        calculatedSubtitle: fallbackSubtitle,
      };
    }

    // Determine the correct currency using the same logic as localized pricing service
    const billingCurrency = currentPlan?.billingApiData?.billingDetails?.currency;
    let currency = 'USD';

    // Priority 1: Use billing currency if available
    if (billingCurrency) {
      currency = billingCurrency;
    } else {
      // Priority 2: Use geo-based currency if localized pricing is enabled
      const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();
      if (isEnabled) {
        // Get currency from plan using geo location
        currency = this.localizedPricingService.getCurrencyFromPlan(currentPlan);
      }
    }

    let addonUnitPrice = addonPlan.unitPrice;
    // Check if there is override price for the specific currency
    if (addonPlan.priceOverrides && addonPlan.priceOverrides.length > 0) {
      const currencyOverride = addonPlan.priceOverrides.find(
        override => override.currencyCode === currency,
      );

      if (currencyOverride) {
        addonUnitPrice = parseFloat(currencyOverride.unitPrice)/100;
      } else {
        // Fallback to first available override if currency-specific override not found
        addonUnitPrice = addonPlan.unitPrice;
      }
    }

    // Calculate the price based on the addon's billing period and current plan's recurrence
    const calculatedPrice = this.formatAddonPrice(addonUnitPrice, currentPlan, addonPlan);
    const calculatedSubtitle = this.formatAddonSubtitle(addonPlan, currentPlan.paymentModel?.paymentRecurrence, fallbackSubtitle);

    return {
      calculatedPrice,
      calculatedSubtitle,
      unitPrice: addonPlan.unitPrice,
      billingPeriod: addonPlan.billingPeriod,
    };
  }

  /**
   * Gets the appropriate addon plan based on current plan's recurrence
   * @param addonIds - The addon IDs for different billing periods
   * @param currentPlan - The current plan with billing API data
   * @returns PlanApiData for the matching addon plan or null
   */
  public getAddonPlanForCurrentRecurrence(addonIds: AddonIds, currentPlan: ConcretePlan): PlanApiData | null {
    if (!addonIds || !currentPlan?.billingApiData?.plans) {
      return null;
    }

    // Get the appropriate addon ID based on current plan's recurrence
    const term = this.getTerms(addonIds, currentPlan);

    if (!term) {
      return null;
    }

    const addonId = addonIds[term];

    if (!addonId) {
      return null;
    }

    // Find the addon plan in the billing API data
    const addonPlan = currentPlan.billingApiData.plans.find(plan =>
      plan.planId === addonId && plan.type === 'add_on',
    );

    return addonPlan || null;
  }

  /**
   * Gets the appropriate term based on the current plan's payment recurrence
   * @param addonIds - The addon IDs for different billing periods
   * @param currentPlan - The current plan
   * @returns The appropriate term key or null
   */
  public getTerms(addonIds: AddonIds, currentPlan: ConcretePlan): keyof AddonIds | null {
    if (!addonIds) {
      return null;
    }

    const availableKeys = Object.keys(addonIds).filter(key =>
      addonIds[key as keyof AddonIds],
    ) as Array<keyof AddonIds>;

    if (availableKeys.length === 0) {
      return null;
    }

    // Check if currentPlan is available before accessing its properties
    if (!currentPlan?.paymentModel?.paymentRecurrence) {
      return null;
    }

    const paymentRecurrence = currentPlan.paymentModel.paymentRecurrence;

    const mappedTerm = this.mapRecurrenceKey[paymentRecurrence];

    if (mappedTerm && availableKeys.includes(mappedTerm)) {
      return mappedTerm;
    }

    return availableKeys[0] || null;
  }

  /**
   * Formats addon price from cents to dollars with localized pricing support
   * @param unitPrice - Price in whole dollars
   * @param currentPlan - Current plan for localized pricing context
   * @param addonPlan - Optional addon plan to check for priceOverrides
   * @returns Formatted price string
   */
  public formatAddonPrice(unitPrice: number, currentPlan?: ConcretePlan, addonPlan?: PlanApiData): string {
    const priceInDollars = Number(unitPrice);
    const billingCurrency = currentPlan?.billingApiData?.billingDetails?.currency;

    // Priority 1: Use billing currency only if available and has matching price override
    if (billingCurrency && currentPlan) {
      // Check if addonPlan has a matching priceOverride for billing currency
      const hasMatchingOverride = addonPlan?.priceOverrides?.some(
        override => override.currencyCode === billingCurrency,
      );

      if (hasMatchingOverride) {
        return this.localizedPricingService.formatPriceWithCurrency(priceInDollars, billingCurrency);
      }
    }

    // Priority 2: Use localized pricing service if enabled and no billing currency
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();
    if (isEnabled && currentPlan) {
      return this.localizedPricingService.formatLocalizedPriceForComponent(priceInDollars, currentPlan);
    }

    // Priority 3: Fallback to USD formatting
    return this.localizedPricingService.formatLocalizedPriceForComponent(priceInDollars, currentPlan, true);
  }

  /**
   * Formats addon subtitle based on current plan's recurrence
   * @param planAddOn - Plan addOn
   * @param currentPlanRecurrence - Current plan's payment recurrence
   * @returns Formatted subtitle string
   */
  public formatAddonSubtitle(planAddOn: PlanApiData, currentPlanRecurrence?: string, fallbackSubtitle?: string): string {

    // If the addon is per account, return 'per account per month/year' based on billing cycle
    if (planAddOn?.isPerAccount) {
      if (currentPlanRecurrence) {
        const recurrenceMap: Record<string, string> = {
          year: this.translate.instant('billing.perAccountPerYear'),
          yearly: this.translate.instant('billing.perAccountPerYear'),
          annual: this.translate.instant('billing.perAccountPerYear'),
          annually: this.translate.instant('billing.perAccountPerYear'),
          month: this.translate.instant('billing.perAccountPerMonth'),
          monthly: this.translate.instant('billing.perAccountPerMonth'),
          quarter: this.translate.instant('billing.perAccountPerQuarter'),
          quarterly: this.translate.instant('billing.perAccountPerQuarter'),
          semi_year: this.translate.instant('billing.perAccountPerSemiYear'),
          semi_yearly: this.translate.instant('billing.perAccountPerSemiYear'),
          'semi-year': this.translate.instant('billing.perAccountPerSemiYear'),
          'semi-annual': this.translate.instant('billing.perAccountPerSemiYear'),
          semiannual: this.translate.instant('billing.perAccountPerSemiYear'),
        };
        return recurrenceMap[currentPlanRecurrence] || fallbackSubtitle || this.translate.instant('billing.perAccountPerMonth');
      }

      // Fallback to billing period mapping for per account addons
      const periodMapPerAccount: Record<string, string> = {
        monthly: this.translate.instant('billing.perAccountPerMonth'),
        yearly: this.translate.instant('billing.perAccountPerYear'),
        quarterly: this.translate.instant('billing.perAccountPerQuarter'),
        semi_yearly: this.translate.instant('billing.perAccountPerSemiYear'),
      };
      return periodMapPerAccount[planAddOn?.billingPeriod] || fallbackSubtitle || this.translate.instant('billing.perAccountPerMonth');
    }

    // If we have current plan recurrence, use it to determine the subtitle
    if (currentPlanRecurrence) {
      const recurrenceMap: Record<string, string> = {
        year: this.translate.instant('common.perUserPerYear'),
        yearly: this.translate.instant('common.perUserPerYear'),
        annual: this.translate.instant('common.perUserPerYear'),
        annually: this.translate.instant('common.perUserPerYear'),
        month: this.translate.instant('common.perUserPerMonth'),
        monthly: this.translate.instant('common.perUserPerMonth'),
        quarter: this.translate.instant('common.perUserPerQuarter'),
        quarterly: this.translate.instant('common.perUserPerQuarter'),
        semi_year: this.translate.instant('common.perUserPerSemiYear'),
        semi_yearly: this.translate.instant('common.perUserPerSemiYear'),
        'semi-year': this.translate.instant('common.perUserPerSemiYear'),
        'semi-annual': this.translate.instant('common.perUserPerSemiYear'),
        semiannual: this.translate.instant('common.perUserPerSemiYear'),
      };

      return recurrenceMap[currentPlanRecurrence] || fallbackSubtitle || this.translate.instant('common.perUserPerMonth');
    }

    // Fallback to billing period mapping
    const periodMapPerUser: Record<string, string> = {
      monthly: this.translate.instant('common.perUserPerMonth'),
      yearly: this.translate.instant('common.perUserPerYear'),
      quarterly: this.translate.instant('common.perUserPerQuarter'),
      semi_yearly: this.translate.instant('common.perUserPerSemiYear'),
    };

    return periodMapPerUser[planAddOn?.billingPeriod] || fallbackSubtitle || this.translate.instant('common.perUserPerMonth');
  }

  /**
   * Checks if an addon is purchased based on subscription plan IDs
   * @param addonIds - The addon IDs for different billing periods
   * @param subscriptionPlanIds - Array of subscribed plan IDs
   * @returns True if addon is purchased
   */
  public isAddonPurchased(addonIds: AddonIds, subscriptionPlanIds: string[]): boolean {
    if (!subscriptionPlanIds || subscriptionPlanIds.length === 0) {
      return false;
    }

    const addonIdValues = Object.values(addonIds);
    return subscriptionPlanIds.some(subscriptionAddonId =>
      addonIdValues.includes(subscriptionAddonId),
    );
  }

  /**
   * Gets subscription plan IDs from current plan's line items
   * @param currentPlan - The current plan
   * @returns Array of subscription plan IDs
   */
  public getSubscriptionPlanIds(currentPlan: ConcretePlan): string[] {
    const lineItems = currentPlan?.billingApiData?.billingDetails?.lineItems || [];

    // Filter out line items with action "remove" or "removed" and map to planIds
    return lineItems
      .filter(item => !item.action || (item.action !== 'remove' && item.action !== 'removed'))
      .map(item => item.planId);
  }
}
